import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import {
    Wrench, Plus, Trash2, Loader2, Save, Cog, Search, Zap, Droplets, Flame, ChevronRight,
} from 'lucide-react';

const SECTION_COLOR = '#0CC0DF';
const SECTION_BG = 'rgba(12,192,223,0.12)';

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

const api = async (url, method = 'GET', body) => {
    const opts = { method, headers: { Accept: 'application/json', 'X-CSRF-TOKEN': csrf() } };
    if (body) { opts.headers['Content-Type'] = 'application/json'; opts.body = JSON.stringify(body); }
    const r = await fetch(url, opts);
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw e; }
    return r.json();
};

export default function Udrzba() {
    const { theme } = useTheme();
    const isDark = theme === 'dark' ||
        (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    const mutedColor = isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)';
    const secondaryColor = isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)';
    const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';

    const cardStyle = {
        background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-raised)',
        backdropFilter: isDark ? 'blur(20px)' : 'none',
        border: `1px solid ${borderColor}`,
    };
    const inputStyle = {
        width: '100%', padding: '8px 12px', borderRadius: 6, fontSize: 13,
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
        background: isDark ? 'rgba(255,255,255,0.06)' : '#fff',
        color: 'var(--text-primary)', outline: 'none',
    };
    const btnPrimary = {
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px',
        borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer',
        background: SECTION_COLOR, color: '#fff', border: 'none',
    };
    const btnSecondary = {
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px',
        borderRadius: 7, fontSize: 13, fontWeight: 500, cursor: 'pointer',
        background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
        color: 'var(--text-primary)', border: 'none',
    };
    const lbl = { color: secondaryColor, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, display: 'block' };

    const AktivniBadge = ({ val }) => (
        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold"
            style={{ background: val ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)', color: val ? '#10B981' : '#EF4444' }}>
            {val ? 'Aktivní' : 'Neaktivní'}
        </span>
    );

    // ── Data ──
    const [tab, setTab] = useState('stroje');
    const [stroje, setStroje] = useState([]);
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    const [selectedId, setSelectedId] = useState(null);
    const [editData, setEditData] = useState(null);
    const [saving, setSaving] = useState(false);
    const [provozniNaklady, setProvozniNaklady] = useState([]);
    const [search, setSearch] = useState('');
    const [activeOnly, setActiveOnly] = useState(false);

    const loadStroje = useCallback(async () => {
        try { setStroje(await api('/api/recepty/stroje')); } catch {}
    }, []);
    const loadProvozniNaklady = useCallback(async () => {
        try { setProvozniNaklady(await api('/api/konfigurace/provozni-naklady')); } catch {}
    }, []);

    useEffect(() => { loadStroje(); loadProvozniNaklady(); }, []);

    const pn = {};
    provozniNaklady.forEach(n => { pn[n.kod] = parseFloat(n.hodnota); });

    const calcNaklad = (s) => {
        const wh = pn.WORKHOURS || 11, ml = pn.MACHLIFE || 5;
        const el = pn.ELECOST || 8, wa = pn.WATCOST || 135, ga = pn.GASCOST || 0;
        const zh = wh * 365 * ml;
        if (zh <= 0 || !s.porizovaci_cena) return null;
        const odpis = s.porizovaci_cena / zh;
        const udrzba = (s.porizovaci_cena * ((s.udrzba_pct || 0) / 100) * ml) / zh;
        const energie = (s.spotreba_el_kwh || 0) * el + (s.spotreba_voda_m3 || 0) * wa + (s.spotreba_plyn_m3 || 0) * ga;
        return (odpis + udrzba + energie).toFixed(2);
    };

    const filteredStroje = useMemo(() => {
        let list = [...stroje];
        if (activeOnly) list = list.filter(s => s.aktivni);
        if (search.trim()) {
            const q = search.toLowerCase().trim();
            list = list.filter(s => (s.nazev || '').toLowerCase().includes(q) || (s.kod || '').toLowerCase().includes(q));
        }
        list.sort((a, b) => a.id - b.id);
        return list;
    }, [stroje, search, activeOnly]);

    const num = (v) => (v === '' || v == null) ? null : parseFloat(v);
    const pickFields = (d) => ({
        kod: d.kod, nazev: d.nazev, porizovaci_cena: num(d.porizovaci_cena), spotreba_el_kwh: num(d.spotreba_el_kwh),
        spotreba_voda_m3: num(d.spotreba_voda_m3), spotreba_plyn_m3: num(d.spotreba_plyn_m3), udrzba_pct: num(d.udrzba_pct),
        aktivni: d.aktivni ?? true, poznamka: d.poznamka || null,
    });

    const nextKod = () => {
        const nums = stroje.map(s => { const m = (s.kod || '').match(/^(?:EQP|STR)[_-](\d+)$/); return m ? parseInt(m[1]) : 0; });
        return `STR_${String(Math.max(0, ...nums) + 1).padStart(4, '0')}`;
    };

    const selectStroj = (s) => {
        setSelectedId(s.id);
        setEditData({ ...pickFields(s), id: s.id });
    };

    const createNew = () => {
        const d = { kod: nextKod(), nazev: '', porizovaci_cena: '', spotreba_el_kwh: '', spotreba_voda_m3: '', spotreba_plyn_m3: '', udrzba_pct: '', aktivni: true, poznamka: '' };
        setSelectedId('new');
        setEditData(d);
    };

    const saveStroj = async () => {
        if (!editData?.nazev?.trim()) { alert('Název je povinný.'); return; }
        setSaving(true);
        try {
            const payload = pickFields(editData);
            if (selectedId === 'new') {
                const created = await api('/api/recepty/stroje', 'POST', payload);
                await loadStroje();
                setSelectedId(created.id);
                setEditData({ ...pickFields(created), id: created.id });
            } else {
                await api(`/api/recepty/stroje/${selectedId}`, 'PUT', payload);
                await loadStroje();
            }
        } catch (e) {
            const msg = e.errors ? Object.values(e.errors).flat().join('\n') : (e.message || e.error || 'Chyba při ukládání');
            alert(msg);
        }
        setSaving(false);
    };

    const delStroj = async () => {
        if (!selectedId || selectedId === 'new') return;
        if (!confirm('Smazat stroj?')) return;
        try {
            await api(`/api/recepty/stroje/${selectedId}`, 'DELETE');
            setSelectedId(null);
            setEditData(null);
            loadStroje();
        } catch {}
    };

    const nh = editData ? calcNaklad(editData) : null;

    return (
        <div className={isMobile ? '' : 'p-6'} style={{ maxWidth: '100%' }}>
            {/* Header — na mobilu se zobrazuje v TopBaru, tady skryjeme */}
            {!isMobile && (
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: SECTION_BG }}>
                        <Wrench size={20} color={SECTION_COLOR} />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Údržba</h1>
                        <p className="text-xs" style={{ color: mutedColor }}>Správa strojů, plány údržby a servisní záznamy</p>
                    </div>
                </div>
            )}

            {/* Jediná záložka pruh nemá — přepínač mezi jednou možností není
                přepínač (norma TabBar). Až přibude druhá, patří do katalogu. */}

            {/* Stroje — left list + right detail */}
            {tab === 'stroje' && (
                <div className="flex gap-4" style={{ height: 'calc(100vh - 180px)', overflow: 'hidden' }}>

                    {/* ── Left panel ── */}
                    <div className="flex flex-col" style={isMobile ? { width: '100%' } : { width: 340, minWidth: 280, flexShrink: 0 }}>
                        {/* Search + filter + add */}
                        <div className="flex gap-2 mb-3">
                            <div className="relative flex-1">
                                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: mutedColor, pointerEvents: 'none' }} />
                                <input value={search} onChange={e => setSearch(e.target.value)}
                                    placeholder="Hledat stroj…"
                                    style={{ ...inputStyle, paddingLeft: 32 }} />
                            </div>
                            <button onClick={() => setActiveOnly(!activeOnly)} title="Jen aktivní"
                                style={{
                                    ...btnSecondary, padding: '8px 10px', fontSize: 11,
                                    background: activeOnly ? SECTION_BG : btnSecondary.background,
                                    color: activeOnly ? SECTION_COLOR : btnSecondary.color,
                                    border: activeOnly ? `1px solid ${SECTION_COLOR}40` : 'none',
                                }}>
                                A
                            </button>
                            <button onClick={createNew} style={{ ...btnPrimary, padding: '8px 10px' }} title="Nový stroj">
                                <Plus size={14} />
                            </button>
                        </div>

                        {/* List */}
                        <div className="rounded-xl overflow-hidden overflow-y-auto flex-1" style={cardStyle}>
                            {filteredStroje.length === 0 ? (
                                <div className="text-center py-12" style={{ color: mutedColor }}>
                                    <Cog size={28} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
                                    <p className="text-xs">{stroje.length === 0 ? 'Žádné stroje.' : 'Nic nenalezeno.'}</p>
                                </div>
                            ) : filteredStroje.map((s, idx) => {
                                const isSelected = selectedId === s.id;
                                const naklad = calcNaklad(s);
                                return (
                                    <div key={s.id} onClick={() => selectStroj(s)}
                                        className="px-3 py-2.5 cursor-pointer"
                                        style={{
                                            borderBottom: idx < filteredStroje.length - 1 ? `1px solid ${borderColor}` : 'none',
                                            borderLeft: isSelected ? `3px solid ${SECTION_COLOR}` : '3px solid transparent',
                                            background: isSelected ? (isDark ? 'rgba(12,192,223,0.08)' : 'rgba(12,192,223,0.06)') : 'transparent',
                                        }}>
                                        <div className="flex items-center gap-2">
                                            <span className="text-[10px] font-mono font-bold" style={{ color: SECTION_COLOR }}>{s.kod}</span>
                                            <span className="text-sm font-medium flex-1 truncate" style={{ color: 'var(--text-primary)' }}>{s.nazev || '(bez nazvu)'}</span>
                                            {!s.aktivni && (
                                                <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold"
                                                    style={{ background: 'rgba(239,68,68,0.15)', color: '#EF4444' }}>Off</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* ── Right panel ── */}
                    <div className="flex-1 min-w-0 flex flex-col">
                        {!editData ? (
                            <div className="flex items-center justify-center flex-1 rounded-xl" style={cardStyle}>
                                <div className="text-center py-20" style={{ color: mutedColor }}>
                                    <Cog size={40} style={{ margin: '0 auto 12px', opacity: 0.2 }} />
                                    <p className="text-sm">Vyber stroj ze seznamu</p>
                                    <p className="text-xs mt-1">nebo vytvoř nový pomocí +</p>
                                </div>
                            </div>
                        ) : (
                            <div className="rounded-xl overflow-hidden flex flex-col flex-1" style={cardStyle}>
                                {/* Detail header */}
                                <div className="flex items-center gap-3 px-5 py-4" style={{ borderBottom: `1px solid ${borderColor}` }}>
                                    <span className="text-xs font-mono font-bold px-2 py-1 rounded" style={{ background: SECTION_BG, color: SECTION_COLOR }}>
                                        {editData.kod || 'STR_????'}
                                    </span>
                                    <span className="text-base font-semibold flex-1" style={{ color: 'var(--text-primary)' }}>
                                        {editData.nazev || (selectedId === 'new' ? 'Nový stroj' : '')}
                                    </span>
                                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: (editData.aktivni ?? true) ? '#10B981' : '#EF4444' }} title={editData.aktivni ? 'Aktivní' : 'Neaktivní'} />
                                    <div className="flex gap-2">
                                        {selectedId !== 'new' && (
                                            <button onClick={delStroj} style={{ ...btnSecondary, padding: '6px 12px', color: '#EF4444' }}>
                                                <Trash2 size={13} /> Smazat
                                            </button>
                                        )}
                                        <button onClick={saveStroj} style={{ ...btnPrimary, padding: '6px 14px' }} disabled={saving}>
                                            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Uložit
                                        </button>
                                    </div>
                                </div>

                                {/* Form body */}
                                <div className="p-5 space-y-5 overflow-y-auto" style={{ flex: 1 }}>
                                    {/* Zakladni info */}
                                    <div>
                                        <h3 className="text-xs font-semibold mb-3 flex items-center gap-2" style={{ color: mutedColor, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            <Cog size={12} /> Základní údaje
                                        </h3>
                                        <div className="grid grid-cols-3 gap-3">
                                            <div>
                                                <label style={lbl}>Kód</label>
                                                <input value={editData.kod || ''} readOnly
                                                    style={{ ...inputStyle, opacity: 0.6, cursor: 'default' }} />
                                            </div>
                                            <div>
                                                <label style={lbl}>Název *</label>
                                                <input value={editData.nazev || ''} placeholder="Název stroje" autoFocus={selectedId === 'new'}
                                                    onChange={e => setEditData({ ...editData, nazev: e.target.value })} style={inputStyle} />
                                            </div>
                                            <div>
                                                <label style={lbl}>Pořizovací cena (Kč)</label>
                                                <input type="number" value={editData.porizovaci_cena || ''} placeholder="0"
                                                    onChange={e => setEditData({ ...editData, porizovaci_cena: e.target.value ? parseFloat(e.target.value) : null })} style={inputStyle} />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Spotreby */}
                                    <div>
                                        <h3 className="text-xs font-semibold mb-3 flex items-center gap-2" style={{ color: mutedColor, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            <Zap size={12} /> Spotřeby a náklady
                                        </h3>
                                        <div className="grid grid-cols-4 gap-3">
                                            <div>
                                                <label style={lbl}><Zap size={10} style={{ display: 'inline', verticalAlign: -1 }} /> Elektřina (kWh)</label>
                                                <input type="number" step="0.01" value={editData.spotreba_el_kwh || ''} placeholder="0"
                                                    onChange={e => setEditData({ ...editData, spotreba_el_kwh: e.target.value ? parseFloat(e.target.value) : null })} style={inputStyle} />
                                            </div>
                                            <div>
                                                <label style={lbl}><Droplets size={10} style={{ display: 'inline', verticalAlign: -1 }} /> Voda (m³/h)</label>
                                                <input type="number" step="0.001" value={editData.spotreba_voda_m3 || ''} placeholder="0"
                                                    onChange={e => setEditData({ ...editData, spotreba_voda_m3: e.target.value ? parseFloat(e.target.value) : null })} style={inputStyle} />
                                            </div>
                                            <div>
                                                <label style={lbl}><Flame size={10} style={{ display: 'inline', verticalAlign: -1 }} /> Plyn (m³/h)</label>
                                                <input type="number" step="0.001" value={editData.spotreba_plyn_m3 || ''} placeholder="0"
                                                    onChange={e => setEditData({ ...editData, spotreba_plyn_m3: e.target.value ? parseFloat(e.target.value) : null })} style={inputStyle} />
                                            </div>
                                            <div>
                                                <label style={lbl}>Údržba (%)</label>
                                                <input type="number" min="0" max="100" step="0.1" value={editData.udrzba_pct || ''} placeholder="5"
                                                    onChange={e => setEditData({ ...editData, udrzba_pct: e.target.value ? parseFloat(e.target.value) : null })} style={inputStyle} />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Naklad info box */}
                                    {nh && (
                                        <div className="rounded-lg p-4 flex items-center gap-4" style={{ background: SECTION_BG, border: `1px solid ${SECTION_COLOR}30` }}>
                                            <div>
                                                <p className="text-xs font-semibold" style={{ color: SECTION_COLOR }}>Odhadovaný náklad</p>
                                                <p className="text-2xl font-bold mt-1" style={{ color: SECTION_COLOR }}>{nh} <span className="text-sm font-normal">Kč/h</span></p>
                                            </div>
                                            <div className="text-[11px] ml-auto" style={{ color: secondaryColor, textAlign: 'right' }}>
                                                <p>Životnost: {(pn.WORKHOURS || 11)} h/den × 365 × {(pn.MACHLIFE || 5)} let</p>
                                                <p>= {((pn.WORKHOURS || 11) * 365 * (pn.MACHLIFE || 5)).toLocaleString()} h celkem</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Poznamka + aktivni */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label style={lbl}>Poznámka</label>
                                            <textarea value={editData.poznamka || ''} placeholder="Volitelná poznámka…" rows={3}
                                                onChange={e => setEditData({ ...editData, poznamka: e.target.value })}
                                                style={{ ...inputStyle, resize: 'vertical' }} />
                                        </div>
                                        <div className="flex items-start pt-6">
                                            <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: 'var(--text-primary)' }}>
                                                <input type="checkbox" checked={editData.aktivni ?? true}
                                                    onChange={e => setEditData({ ...editData, aktivni: e.target.checked })}
                                                    style={{ accentColor: SECTION_COLOR, width: 16, height: 16 }} />
                                                Aktivní stroj
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
